import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';

import { trendsQueryTrends } from '@repo/sdk/funcs/trendsQueryTrends';
import {
  QueryTrendSourceResult,
  QueryTrendSourceResultStatus,
  QueryTrendsRequestBody,
  QueryTrendsResult,
  TrendValue,
} from '@repo/sdk/models/components';
import { SDKError } from '@repo/sdk/models/errors';
import { QueryTrendsRequest } from '@repo/sdk/models/operations';

import { GaiaClientFunction } from '@/client/types';
import { createUTCDate, getUTCNow, subtractUTCMilliseconds } from '@/helpers/datetime/utc-date';
import { GaiaQueryWithAgentId } from '@/queries/types';

/** Trailing window for incremental live fetches (ms). */
export const LIVE_DELTA_MS = 10_000;

/** Trailing window spans this many sampling periods (need at least one bucket boundary for aggregation). */
const LIVE_DELTA_SAMPLING_PERIODS = 2;

/** Minimum trailing window; wider intervals need longer windows so aggregation yields points. */
export function computeLiveDeltaMs(samplingIntervalSecs: number): number {
  return Math.max(LIVE_DELTA_MS, samplingIntervalSecs * 1000 * LIVE_DELTA_SAMPLING_PERIODS);
}

/**
 * Computes the latest bucket boundary (aligned to the original query start)
 * that is at or before `nowMs - deltaMs`.
 */
export function computeAlignedStartMs(
  startMs: number,
  nowMs: number,
  intervalMs: number,
  deltaMs: number
): number | undefined {
  if (!Number.isFinite(startMs) || !Number.isFinite(nowMs) || intervalMs <= 0) {
    return undefined;
  }

  const target = nowMs - deltaMs;
  if (target < startMs) {
    return undefined;
  }

  const buckets = Math.floor((target - startMs) / intervalMs);
  return startMs + buckets * intervalMs;
}

export const REFETCH_BUFFER_MS = 5000;

/**
 * Computes ms until the next bucket boundary (aligned to query start) plus
 * a buffer for backend processing. Returns undefined if inputs are invalid,
 * signaling the caller to use a fallback.
 */
export function computeLiveRefetchInterval(
  startMs: number,
  nowMs: number,
  intervalMs: number
): number | undefined {
  if (!Number.isFinite(startMs) || !Number.isFinite(nowMs) || intervalMs <= 0) {
    return undefined;
  }
  const elapsed = nowMs - startMs;
  const periodsElapsed = Math.ceil(elapsed / intervalMs);
  let nextBoundaryMs = startMs + periodsElapsed * intervalMs;
  if (nextBoundaryMs <= nowMs) {
    nextBoundaryMs += intervalMs;
  }
  return nextBoundaryMs - nowMs + REFETCH_BUFFER_MS;
}

function toISOOrIdentity(val: Date | string | undefined): string | undefined {
  if (val === undefined) return undefined;
  return val instanceof Date ? val.toISOString() : val;
}

/**
 * Canonical representation of a query body for cache key hashing.
 * Sources are sorted; dates are ISO strings; optional fields have explicit defaults.
 */
function canonicalBodyForHash(body: QueryTrendsRequestBody): string {
  const sortedSources = [...body.sources].sort(
    (a, b) =>
      (a.sourceType ?? '').localeCompare(b.sourceType ?? '') ||
      (a.id ?? '').localeCompare(b.id ?? '') ||
      (a.valueKind ?? '').localeCompare(b.valueKind ?? '')
  );

  const canonical = {
    start: body.start instanceof Date ? body.start.toISOString() : body.start,
    end: toISOOrIdentity(body.end),
    facilityTimezone: body.facilityTimezone,
    samplingInterval: body.samplingInterval,
    includeNormalized: body.includeNormalized ?? false,
    sources: sortedSources,
  };

  return JSON.stringify(canonical);
}

/**
 * Produces a stable string key for the request body so that semantically
 * identical requests share the same cache entry (for historical queries).
 */
export function hashQueryTrendsBody(body: QueryTrendsRequestBody): string {
  return canonicalBodyForHash(body);
}

function hashLiveWindowQueryTrendsBody(body: QueryTrendsRequestBody, liveWindowMs: number): string {
  const canonical = JSON.parse(canonicalBodyForHash(body)) as Record<string, unknown>;
  canonical.start = `live-window:${liveWindowMs}`;
  return JSON.stringify(canonical);
}

function trendValueTimestampMs(value: TrendValue): number {
  const ts = value.timestamp;
  return ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
}

function cloneTrendValue(value: TrendValue): TrendValue {
  const ts = value.timestamp;
  const ms = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
  return {
    ...value,
    timestamp: new Date(ms),
  };
}

function mergeQueryTrendSourceResults(
  base: QueryTrendSourceResult,
  incoming: QueryTrendSourceResult
): QueryTrendSourceResult {
  const byEpochMs = new Map<number, TrendValue>();
  for (const v of base.values) {
    byEpochMs.set(trendValueTimestampMs(v), cloneTrendValue(v));
  }
  for (const v of incoming.values) {
    byEpochMs.set(trendValueTimestampMs(v), cloneTrendValue(v));
  }
  const sortedMs = [...byEpochMs.keys()].sort((a, b) => a - b);
  const values = sortedMs.map((ms) => byEpochMs.get(ms)!);
  // Incremental live fetches can return no_data with an empty window while cache still
  // holds prior points; status must reflect merged values so charts don't drop series.
  const status = values.length > 0 ? QueryTrendSourceResultStatus.Data : incoming.status;
  return {
    aggType: incoming.aggType,
    status,
    values,
  };
}

function toEpochMs(value: Date | string | undefined): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  try {
    const ms = createUTCDate(value).getTime();
    return Number.isFinite(ms) ? ms : undefined;
  } catch {
    return undefined;
  }
}

function computeLiveWindowMs(start: Date | string | undefined, nowMs: number): number | undefined {
  const startMs = toEpochMs(start);
  if (startMs === undefined) {
    return undefined;
  }
  const windowMs = nowMs - startMs;
  if (!Number.isFinite(windowMs) || windowMs <= 0) {
    return undefined;
  }
  return windowMs;
}

function trimQueryTrendsResultByCutoff(
  result: QueryTrendsResult,
  cutoffMs: number
): QueryTrendsResult {
  const sources: QueryTrendsResult['sources'] = {};
  for (const sourceId of Object.keys(result.sources)) {
    const source = result.sources[sourceId];
    sources[sourceId] = {
      aggType: source.aggType,
      status: source.status,
      values: source.values
        .filter((value) => trendValueTimestampMs(value) >= cutoffMs)
        .map(cloneTrendValue),
    };
  }
  return {
    interval: result.interval,
    sources,
  };
}

/**
 * Merges a prior `query_trends` result with a newer fetch. Per source, points are
 * de-duplicated by epoch milliseconds; the incoming point wins when timestamps collide.
 * Merged `values` are sorted ascending by timestamp. When both sides contribute to a
 * source, `aggType` follows the incoming result; `status` is `data` if merged values are
 * non-empty, otherwise the incoming `status`. Sources present only in `base` are
 * retained unchanged. Top-level `interval` follows the incoming result.
 */
export function mergeQueryTrendsResults(
  base: QueryTrendsResult,
  incoming: QueryTrendsResult
): QueryTrendsResult {
  const sources: QueryTrendsResult['sources'] = {};

  for (const sourceId of Object.keys(base.sources)) {
    const baseSource = base.sources[sourceId];
    const incomingSource = incoming.sources[sourceId];
    if (!incomingSource) {
      sources[sourceId] = {
        aggType: baseSource.aggType,
        status: baseSource.status,
        values: baseSource.values.map(cloneTrendValue),
      };
      continue;
    }
    sources[sourceId] = mergeQueryTrendSourceResults(baseSource, incomingSource);
  }

  for (const sourceId of Object.keys(incoming.sources)) {
    if (sources[sourceId] !== undefined) {
      continue;
    }
    const inc = incoming.sources[sourceId];
    sources[sourceId] = {
      aggType: inc.aggType,
      status: inc.status,
      values: inc.values.map(cloneTrendValue),
    };
  }

  return {
    interval: incoming.interval,
    sources,
  };
}

/**
 * TanStack Query factory for the query_trends endpoint.
 * Uses a hybrid cache key: live queries are keyed by body hash (charts with identical bodies share cache);
 * historical queries are keyed only by body hash for deduplication.
 *
 * `queryClient` is required for all calls: live mode uses it for cache read/merge; historical mode ignores it.
 */
export type QueryTrendsQueryProps<TData = QueryTrendsResult> = GaiaQueryWithAgentId & {
  body: QueryTrendsRequestBody;
  client: GaiaClientFunction;
  liveWindowMs?: number;
  queryClient: QueryClient;
  select?: (data: QueryTrendsResult) => TData;
};

export function queryTrendsQuery<TData = QueryTrendsResult>({
  agentId,
  body,
  client,
  liveWindowMs,
  queryClient,
  select,
}: QueryTrendsQueryProps<TData>): UseQueryOptions<QueryTrendsResult, SDKError, TData> {
  const isLive = body.end === undefined;
  const normalizedBodyHash =
    isLive && liveWindowMs !== undefined
      ? hashLiveWindowQueryTrendsBody(body, liveWindowMs)
      : hashQueryTrendsBody(body);

  const queryKey = isLive
    ? ([agentId, 'queryTrends', 'live', normalizedBodyHash] as const)
    : ([agentId, 'queryTrends', 'historical', normalizedBodyHash] as const);

  return {
    queryKey,
    queryFn: async () => {
      if (!isLive) {
        return client<QueryTrendsRequest, QueryTrendsResult>({
          gaiaFunc: trendsQueryTrends,
          request: { agentId: agentId!, body },
        });
      }

      const liveBody: QueryTrendsRequestBody =
        liveWindowMs === undefined
          ? body
          : {
              ...body,
              start: subtractUTCMilliseconds(liveWindowMs),
            };

      const cached = queryClient.getQueryData<QueryTrendsResult>(queryKey);
      if (!cached) {
        return client<QueryTrendsRequest, QueryTrendsResult>({
          gaiaFunc: trendsQueryTrends,
          request: { agentId: agentId!, body: liveBody },
        });
      }

      const now = getUTCNow();
      const nowMs = now.getTime();
      const intervalMs = liveBody.samplingInterval * 1000;
      const deltaMs = computeLiveDeltaMs(liveBody.samplingInterval);
      const startMs = toEpochMs(liveBody.start);
      const alignedStartMs =
        startMs !== undefined
          ? computeAlignedStartMs(startMs, nowMs, intervalMs, deltaMs)
          : undefined;
      const alignedStart =
        alignedStartMs !== undefined
          ? createUTCDate(alignedStartMs)
          : subtractUTCMilliseconds(deltaMs, now);
      const incrementalBody: QueryTrendsRequestBody = {
        ...liveBody,
        start: alignedStart,
        end: now,
      };
      const incoming = await client<QueryTrendsRequest, QueryTrendsResult>({
        gaiaFunc: trendsQueryTrends,
        request: { agentId: agentId!, body: incrementalBody },
      });
      const merged = mergeQueryTrendsResults(cached, incoming);
      const resolvedLiveWindowMs = liveWindowMs ?? computeLiveWindowMs(liveBody.start, nowMs);
      if (resolvedLiveWindowMs === undefined) {
        return merged;
      }
      const cutoffMs = nowMs - resolvedLiveWindowMs;
      return trimQueryTrendsResultByCutoff(merged, cutoffMs);
    },
    enabled: !!agentId && !!body && (body.sources?.length ?? 0) > 0,
    refetchInterval: isLive
      ? () => {
          const nowMs = getUTCNow().getTime();
          const intervalMs = body.samplingInterval * 1000;
          const startMs = toEpochMs(body.start);
          if (startMs === undefined) {
            return intervalMs / 4;
          }
          return computeLiveRefetchInterval(startMs, nowMs, intervalMs) ?? intervalMs / 4;
        }
      : false,
    refetchIntervalInBackground: isLive,
    staleTime: isLive ? 0 : 5 * 60 * 1000,
    ...(select !== undefined ? { select } : {}),
  };
}
